// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { useCallback, useEffect, useRef } from 'react';
import { Tooltip } from '@mui/material';
import { classNames } from '@shared/ui-kit';
import { IconButton } from '@shared/ui-kit/components/icon-button';
import styles from './accounts-selector.module.css';
import { isUndefined } from '@shared/utils';

export interface AccountsSelectorTagOption {
  readonly id: string;
  readonly name: string;
  readonly isActive: boolean;
}

export interface AccountsSelectorTagsProps {
  readonly tagOptions: readonly AccountsSelectorTagOption[];
  readonly onTagSelect?: (tagId: string) => void;
  readonly open: boolean;
  readonly activeTagId: string | undefined;
}

const SCROLL_AMOUNT = 250;

export const AccountsSelectorTags = ({
  tagOptions,
  onTagSelect,
  open,
  activeTagId,
}: AccountsSelectorTagsProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const container = scrollRef.current;

    if (!container) return;

    const frameId = requestAnimationFrame(() => {
      const isAll = activeTagId === '' || isUndefined(activeTagId);

      if (isAll) {
        container.scrollTo({ left: 0, behavior: 'instant' });
      } else {
        const activeButton = container.querySelector<HTMLElement>(
          '[aria-pressed="true"]',
        );
        activeButton?.scrollIntoView({
          inline: 'center',
          block: 'nearest',
          behavior: 'instant',
        });
      }
    });

    return () => cancelAnimationFrame(frameId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const scroll = useCallback((direction: 'left' | 'right') => {
    scrollRef.current?.scrollBy({
      left: direction === 'left' ? -SCROLL_AMOUNT : SCROLL_AMOUNT,
      behavior: 'smooth',
    });
  }, []);

  return (
    <div
      className={styles['accounts-tags-scroll-wrapper']}
      data-testid="accounts-selector-tags"
    >
      <span className={styles['accounts-tags-arrow-placeholder']}>
        <IconButton
          variant="icon"
          icon="ArrowLeft"
          className={styles['accounts-tags-scroll-btn']}
          size="xl"
          onClick={() => scroll('left')}
          data-testid="accounts-tags-scroll-left"
        />
      </span>

      <div
        ref={scrollRef}
        className={styles['accounts-tags-scroll']}
        data-testid="accounts-tags-scroll-container"
      >
        <div className={styles['accounts-popover-tags-list']}>
          {tagOptions.map(tag => (
            <Tooltip key={tag.id || 'all'} title={tag.name}>
              <button
                type="button"
                className={classNames(
                  styles['accounts-tag-trigger'],
                  tag.isActive && styles['accounts-tag-trigger--active'],
                )}
                aria-pressed={tag.isActive}
                onClick={() => onTagSelect?.(tag.id)}
              >
                {tag.name}
              </button>
            </Tooltip>
          ))}
        </div>
      </div>

      <span className={styles['accounts-tags-arrow-placeholder']}>
        <IconButton
          variant="icon"
          icon="ArrowRight"
          className={styles['accounts-tags-scroll-btn-right']}
          size="xl"
          onClick={() => scroll('right')}
          data-testid="accounts-tags-scroll-right"
        />
      </span>
    </div>
  );
};
